/**
 * Module dependencies.
 */

import { ifProp, prop } from 'styled-tools';
import { media } from 'src/styles/media';
import { normalizeNewLine } from 'src/core/utils/string';
import { useEffect, useMemo, useRef, useState } from 'react';
import RawHtml from 'src/components/core/raw-html';
import styled, { css } from 'styled-components';
import useBreakpoint from 'src/hooks/use-breakpoint';

/**
 * `Props` type.
 */

type Props = {
  className?: string;
  hasDarkTheme?: boolean;
  text: string;
};

/**
 * Constants.
 */

const charSize = 10.4;
const lineHeight = 29.3;

/**
 * `Wrapper` styled component.
 */

const Wrapper = styled.div<{
  columnsWidth?: number;
  totalColumns?: number;
}>`
  height: 100%;

  @media (min-width: 992px) {
    width: calc(${prop('totalColumns', 1)} * ${prop('columnsWidth')}vw + (${prop('totalColumns', 1)} - 1) * 2rem);
  }
`;

/**
 * `StyledParagraph` styled component.
 */

const StyledParagraph = styled(RawHtml)<{
  columnsWidth?: number;
  hasDarkTheme?: boolean;
  totalColumns?: number;
}>`
  color: var(--color-grey-700);
  font-family: var(--font-futura-pt);
  font-size: 1rem;
  height: 100%;
  line-height: 1.25rem;

  ${media.min.md`
    font-size: 1.1875rem;
    line-height: 1.8125rem;
  `}

  ${ifProp(
    'hasDarkTheme',
    css`
      color: var(--color-grey-300);
      font-family: var(--font-baskerville);
    `
  )}

  @media (min-width: 992px) {
    column-fill: auto;
    column-gap: 1.75rem;
    columns: ${prop('columnsWidth')}vw ${prop('totalColumns')};
    will-change: columns;
  }

  span {
    float: right;
    margin-top: 1rem;
  }
`;

/**
 * Count total lines.
 */

const countLines = (text: string, maxChars: number) => {
  let totalChars = 0;
  const words = text.replaceAll('<br>', ' <br> ').split(' ');

  return words.reduce((totalLines, word) => {
    if (word === '<br>' || totalChars + word.length > maxChars) {
      totalChars = 0;

      return totalLines + 1;
    }

    totalChars += word.length;

    return totalLines;
  }, 0);
};

/**
 * `ColumnsText` component.
 */

const ColumnsText = ({ className, hasDarkTheme, text }: Props) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const isDesktop = useBreakpoint('xl', 'min');
  const isLargeDesktop = useBreakpoint('xxl', 'min');
  const [totalColumns, setTotalColumns] = useState<number>();
  const columnsWidth = useMemo(() => (isLargeDesktop && 27) || (isDesktop && 40) || 44, [isDesktop, isLargeDesktop]);

  useEffect(() => {
    function calculateTotalColumns() {
      if (!columnsWidth || !wrapperRef?.current) {
        return;
      }

      const maxCharsPerLine = Math.floor((window.innerWidth * columnsWidth) / 100 / charSize);

      const normalizedText = normalizeNewLine(text) as string;
      const totalLines = countLines(normalizedText, maxCharsPerLine);
      const paragraphsHeight = wrapperRef?.current.getBoundingClientRect().height;

      setTotalColumns(Math.ceil((totalLines * lineHeight) / paragraphsHeight));
    }

    calculateTotalColumns();

    window.addEventListener('resize', calculateTotalColumns);

    return () => removeEventListener('resize', calculateTotalColumns);
  }, [columnsWidth, text]);

  return (
    <Wrapper className={className} columnsWidth={columnsWidth + 1} ref={wrapperRef} totalColumns={totalColumns}>
      <StyledParagraph
        columnsWidth={columnsWidth}
        element={'p'}
        hasDarkTheme={hasDarkTheme}
        totalColumns={totalColumns}
      >
        {normalizeNewLine(text)}
      </StyledParagraph>
    </Wrapper>
  );
};

/**
 * Export `ColumnsText` component.
 */

export default ColumnsText;
